export interface ListingLevelImageTrackerResponse {
    warnings: [];
    coveragePercentage: number;
    trackedDistributors: ListingImageTrackerDistributorDetails[];
}

export interface ListingImageTrackerDistributorDetails {
    did: number;
    name: string;
    channelName: string;
    channelLogo: string;
    channelLogoUrl: string;
    eligibleImagesCount: number;
    exportedImagesCount: number;
    percentage: number;
    exportedDate: Date;
    errors: ImageTrackerListingError[];
    errorTranslationKeys: string[];
    warnings: ImageTrackerListingWarning[];
    warningTranslationKeys: string[];
}

export interface ListingAssetImageTrackerDetails {
    publicId: number;
    index: number;
    caption: string;
    exportedDate: Date;
    warning: ImageTrackerAssetWarning;
    warningTranslationKey: string;
}

export enum ImageTrackerStatusType {
    UNKNOWN = 'unknown',
    FAILED = 'failed',
    SKIPPED = 'skipped',
    EXPORTED_WITH_WARNINGS = 'exportedWithWarnings',
    SUCCESSFUL = 'successful',
}
export const IMAGE_TRACKER_STATUS_TYPE_TRANSLATIONS: Record<ImageTrackerStatusType, string> = {
    [ImageTrackerStatusType.UNKNOWN]: 'listing.visuals.imageTracker.asset.warningCategories.unknown',
    [ImageTrackerStatusType.FAILED]: 'listing.visuals.imageTracker.asset.warningCategories.failed',
    [ImageTrackerStatusType.SKIPPED]: 'listing.visuals.imageTracker.asset.warningCategories.skipped',
    [ImageTrackerStatusType.EXPORTED_WITH_WARNINGS]: 'listing.visuals.imageTracker.asset.warningCategories.exportedWithWarningsc',
    [ImageTrackerStatusType.SUCCESSFUL]: 'listing.visuals.imageTracker.asset.warningCategories.successful',
};

export enum ImageTrackerListingWarning {
    LISTING_GENERIC_WARNING = 0,
    PHOTOS_EXCEED_EXPORT_LIMIT = 1,
}
export const IMAGE_TRACKER_LISTING_WARNINGS_TRANSLATIONS: Record<ImageTrackerListingWarning, string> = {
    [ImageTrackerListingWarning.LISTING_GENERIC_WARNING]: 'listing.visuals.imageTracker.listing.warnings.genericWarning',
    [ImageTrackerListingWarning.PHOTOS_EXCEED_EXPORT_LIMIT]: 'listing.visuals.imageTracker.listing.warnings.photosExceedExportLimit',
};

export enum ImageTrackerListingError {
    LISTING_FAILED_TO_EXPORT = 0,
    DISTRIBUTOR_NOT_TRACKED = 1,
    LISTING_UNPUBLISHED_BY_SUPPLIER = 2,
    SUPPLIER_CHAIN_CODE_ERROR = 3,
    LISTING_RESTRICTED_BY_SUPPLIER = 4,
    LISTING_DISTRIBUTOR_CHAIN_CODE_INVALID = 5,
    LISTING_DISTRIBUTOR_MAPPED_ID_INVALID = 6,
    NO_ACTIVATED_MEDIA_GALLERY_TO_EXPORT = 7,
    CANNOT_EXPORT_TEST_PROPERTY = 8,
    LISTING_DISTRIBUTOR_MAPPED_ID_MISSING = 9,
    PROPERTY_DELETED = 10,

    PROPERTY_SKIPPED_BRAND_RESTRICTION = 17,
    PROPERTY_NOT_FOUND_IN_DISTRIBUTORS_PROPERTY_LIST = 18,
    DELIVERY_FAILED = 19,
    LISTING_DISTRIBUTOR_ID_INVALID_OR_LISTING_NOT_WHITELISTED_IN_DISTRIBUTOR_ACCOUNT = 20,
    LISTING_DISTRIBUTOR_CHAIN_CODE_MISSING = 21,
    LISTING_GENERIC_ERROR = 22,
}
export const IMAGE_TRACKER_LISTING_ERRORS_TRANSLATIONS: Record<ImageTrackerListingError, string> = {
    [ImageTrackerListingError.LISTING_FAILED_TO_EXPORT]: 'listing.visuals.imageTracker.listing.errors.listingFailedToExport',
    [ImageTrackerListingError.DISTRIBUTOR_NOT_TRACKED]: 'listing.visuals.imageTracker.listing.errors.distributorNotTracked',
    [ImageTrackerListingError.LISTING_UNPUBLISHED_BY_SUPPLIER]: 'listing.visuals.imageTracker.listing.errors.listingUnpublishedBySupplier',
    [ImageTrackerListingError.SUPPLIER_CHAIN_CODE_ERROR]: 'listing.visuals.imageTracker.listing.errors.supplierChainCodeError',
    [ImageTrackerListingError.LISTING_RESTRICTED_BY_SUPPLIER]: 'listing.visuals.imageTracker.listing.errors.listingRestrictedBySupplier',
    [ImageTrackerListingError.LISTING_DISTRIBUTOR_CHAIN_CODE_INVALID]:
        'listing.visuals.imageTracker.listing.errors.listingDistributorChainCodeInvalid',
    [ImageTrackerListingError.LISTING_DISTRIBUTOR_MAPPED_ID_INVALID]:
        'listing.visuals.imageTracker.listing.errors.listingDistributorMappedIdInvalid',
    [ImageTrackerListingError.NO_ACTIVATED_MEDIA_GALLERY_TO_EXPORT]:
        'listing.visuals.imageTracker.listing.errors.noActivatedMediaGalleryToExport',
    [ImageTrackerListingError.CANNOT_EXPORT_TEST_PROPERTY]: 'listing.visuals.imageTracker.listing.errors.cannotExportTestProperty',
    [ImageTrackerListingError.LISTING_DISTRIBUTOR_MAPPED_ID_MISSING]:
        'listing.visuals.imageTracker.listing.errors.listingDistributorMappedIdMissing',
    [ImageTrackerListingError.PROPERTY_DELETED]: 'listing.visuals.imageTracker.listing.errors.propertyDeleted',
    [ImageTrackerListingError.PROPERTY_SKIPPED_BRAND_RESTRICTION]:
        'listing.visuals.imageTracker.listing.errors.propertySkippedDueToBrandRestriction',
    [ImageTrackerListingError.PROPERTY_NOT_FOUND_IN_DISTRIBUTORS_PROPERTY_LIST]:
        'listing.visuals.imageTracker.listing.errors.propertyNotFoundInDistributorsPropertyList',
    [ImageTrackerListingError.DELIVERY_FAILED]: 'listing.visuals.imageTracker.listing.errors.deliveryFailed',
    [ImageTrackerListingError.LISTING_DISTRIBUTOR_ID_INVALID_OR_LISTING_NOT_WHITELISTED_IN_DISTRIBUTOR_ACCOUNT]:
        'listing.visuals.imageTracker.listing.errors.listingDistributorIdInvalidOrListingNotWhitelistedInDistributorAccount',
    [ImageTrackerListingError.LISTING_DISTRIBUTOR_CHAIN_CODE_MISSING]:
        'listing.visuals.imageTracker.listing.errors.listingDistributorChainCodeMissing',
    [ImageTrackerListingError.LISTING_GENERIC_ERROR]: 'listing.visuals.imageTracker.listing.errors.listingGenericError',
};

export enum ImageTrackerAssetWarning {
    GENERIC_WARNING = 0,
    ASSET_AGE_EXCEEDS_SUPPLIER_CONFIG = 1,
    CATEGORY_UNDEFINED = 2,
    SIZE_QUALITY_SCORE_TOO_LOW = 3,
    NO_ROOM_TYPE_ASSIGNED = 4,
    NON_SPHERICAL_HD360 = 5,
    DISABLED_ASSET = 6,
    NOT_APPROVED_ASSET = 7,
    IMAGE_TRACKER_IMAGE_DISABLED = 8,
    IMAGE_TRACKER_MIN_SIZE = 9,
    IMAGE_TRACKER_UNCATEGORIZED = 10,
    IMAGE_TRACKER_RESTRICTED_CATEGORY_FOR_DP = 11,
    IMAGE_TRACKER_UNCAPTIONED = 12,
    IMAGE_TRACKER_INVALID_ASPECT_RATIO = 13,
    IMAGE_TRACKER_DP_SPECIFIC_REASON = 14,
    IMAGE_TRACKER_RESTRICTED_PRECICE_TAG_FOR_DP = 15,
    IMAGE_TRACKER_DISTRIBUTE_TO_MY_BRAND_ONLY = 16,
    IMAGE_TRACKER_NO_ROOM_CODE_ASSIGNED = 17,
    IMAGE_TRACKER_DP_DISABLED = 18,
    IMAGE_TRACKER_FAILED_TO_EXPORT = 19,
    IMAGE_TRACKER_INNER_EXCEPTION = 20,
    IMAGE_TRACKER_RESTRICTED_TAGS_FOR_DP = 21,
    IMAGE_TRACKER_PROPERTY_LEVEL_RESTRICTION = 22,
    IMAGE_TRACKER_UNCAPTIONED_FOR_OTHER_LANGUAGES = 23,
    IMAGE_TRACKER_ASSET_LEVEL_DISTRIBUTOR_ACCESS_RESTRICTIONS = 24,
    IMAGE_TRACKER_PHOTO_NOT_APPROVED = 25,
    IMAGE_TRACKER_INVALID_ROOM_CODE = 26,
    IMAGE_TRACKER_EXCEEDS_EXPORT_LIMIT = 27,
}
export const IMAGE_TRACKER_ASSET_WARNINGS_TRANSLATIONS: Record<ImageTrackerAssetWarning, string> = {
    [ImageTrackerAssetWarning.GENERIC_WARNING]: 'listing.visuals.imageTracker.asset.warnings.genericWarning',
    [ImageTrackerAssetWarning.ASSET_AGE_EXCEEDS_SUPPLIER_CONFIG]:
        'listing.visuals.imageTracker.asset.warnings.assetAgeExceedsSupplierConfiguration',
    [ImageTrackerAssetWarning.CATEGORY_UNDEFINED]: 'listing.visuals.imageTracker.asset.warnings.categoryUndefined',
    [ImageTrackerAssetWarning.SIZE_QUALITY_SCORE_TOO_LOW]: 'listing.visuals.imageTracker.asset.warnings.sizeQualityScoreTooLow',
    [ImageTrackerAssetWarning.NO_ROOM_TYPE_ASSIGNED]: 'listing.visuals.imageTracker.asset.warnings.noRoomTypeAssigned',
    [ImageTrackerAssetWarning.NON_SPHERICAL_HD360]: 'listing.visuals.imageTracker.asset.warnings.nonSphericalHD360',
    [ImageTrackerAssetWarning.DISABLED_ASSET]: 'listing.visuals.imageTracker.asset.warnings.assetDisabled',
    [ImageTrackerAssetWarning.NOT_APPROVED_ASSET]: 'listing.visuals.imageTracker.asset.warnings.assetNotApproved',
    [ImageTrackerAssetWarning.IMAGE_TRACKER_IMAGE_DISABLED]: 'listing.visuals.imageTracker.asset.warnings.imageDisabled',
    [ImageTrackerAssetWarning.IMAGE_TRACKER_MIN_SIZE]: 'listing.visuals.imageTracker.asset.warnings.minSize',
    [ImageTrackerAssetWarning.IMAGE_TRACKER_UNCATEGORIZED]: 'listing.visuals.imageTracker.asset.warnings.uncategorized',
    [ImageTrackerAssetWarning.IMAGE_TRACKER_RESTRICTED_CATEGORY_FOR_DP]:
        'listing.visuals.imageTracker.asset.warnings.restrictedCategoryForDP',
    [ImageTrackerAssetWarning.IMAGE_TRACKER_UNCAPTIONED]: 'listing.visuals.imageTracker.asset.warnings.uncaptioned',
    [ImageTrackerAssetWarning.IMAGE_TRACKER_INVALID_ASPECT_RATIO]: 'listing.visuals.imageTracker.asset.warnings.invalidAspectRatio',
    [ImageTrackerAssetWarning.IMAGE_TRACKER_DP_SPECIFIC_REASON]: 'listing.visuals.imageTracker.asset.warnings.DPSpecificReason',
    [ImageTrackerAssetWarning.IMAGE_TRACKER_RESTRICTED_PRECICE_TAG_FOR_DP]:
        'listing.visuals.imageTracker.asset.warnings.restrictedPrecIceTagForDP',
    [ImageTrackerAssetWarning.IMAGE_TRACKER_DISTRIBUTE_TO_MY_BRAND_ONLY]:
        'listing.visuals.imageTracker.asset.warnings.distributionIsMyBrandOnly',
    [ImageTrackerAssetWarning.IMAGE_TRACKER_NO_ROOM_CODE_ASSIGNED]: 'listing.visuals.imageTracker.asset.warnings.noRoomCodeAssigned',
    [ImageTrackerAssetWarning.IMAGE_TRACKER_DP_DISABLED]: 'listing.visuals.imageTracker.asset.warnings.DPDisabled',
    [ImageTrackerAssetWarning.IMAGE_TRACKER_FAILED_TO_EXPORT]: 'listing.visuals.imageTracker.asset.warnings.failedToExport',
    [ImageTrackerAssetWarning.IMAGE_TRACKER_INNER_EXCEPTION]: 'listing.visuals.imageTracker.asset.warnings.innerException',
    [ImageTrackerAssetWarning.IMAGE_TRACKER_RESTRICTED_TAGS_FOR_DP]: 'listing.visuals.imageTracker.asset.warnings.restrictedTagsForDP',
    [ImageTrackerAssetWarning.IMAGE_TRACKER_PROPERTY_LEVEL_RESTRICTION]:
        'listing.visuals.imageTracker.asset.warnings.propertyLevelRestriction',
    [ImageTrackerAssetWarning.IMAGE_TRACKER_UNCAPTIONED_FOR_OTHER_LANGUAGES]:
        'listing.visuals.imageTracker.asset.warnings.uncaptionedForOtherLanguages',
    [ImageTrackerAssetWarning.IMAGE_TRACKER_ASSET_LEVEL_DISTRIBUTOR_ACCESS_RESTRICTIONS]:
        'listing.visuals.imageTracker.asset.warnings.assetLevelDistributorAccessRestriction',
    [ImageTrackerAssetWarning.IMAGE_TRACKER_PHOTO_NOT_APPROVED]: 'listing.visuals.imageTracker.asset.warnings.photoNotApproved',
    [ImageTrackerAssetWarning.IMAGE_TRACKER_INVALID_ROOM_CODE]: 'listing.visuals.imageTracker.asset.warnings.invalidRoomCode',
    [ImageTrackerAssetWarning.IMAGE_TRACKER_EXCEEDS_EXPORT_LIMIT]: 'listing.visuals.imageTracker.asset.warnings.exceedsExportLimit',
};

export function getImageTrackerStatusTypeFromAssetWarning(warningType: ImageTrackerAssetWarning | null) {
    switch (warningType) {
        case null:
            return ImageTrackerStatusType.SUCCESSFUL;
        case ImageTrackerAssetWarning.IMAGE_TRACKER_FAILED_TO_EXPORT:
        case ImageTrackerAssetWarning.IMAGE_TRACKER_INNER_EXCEPTION:
            return ImageTrackerStatusType.FAILED;
        case ImageTrackerAssetWarning.IMAGE_TRACKER_INVALID_ROOM_CODE:
            return ImageTrackerStatusType.EXPORTED_WITH_WARNINGS;
        default:
            return ImageTrackerStatusType.SKIPPED;
    }
}
